import { createContext } from 'react';

import type { ISize } from '~/helpers/types';
import type { IAppPreviewRoute } from './AppPreviewState';

// Why splitting state and dispatch into two distinct contexts? There are two
// main benefits to this approach:
// 1. Each context value is now stable (the dispatch function never changes,
//    and the state is only updated when an action is dispatched).
// 2. Some consumer components only need to dispatch actions, and not read the
//    state (ie. only navigate to a new path).
// See:
// - https://lokeshsahukar.medium.com/optimize-react-context-api-state-management-and-avoid-unnecessary-re-renders-4511ef9cb351
// - https://kentcdodds.com/blog/how-to-optimize-your-context-value

export type IAppPreviewActionOptions = {
  persistent?: boolean;
};

export type IAppPreviewOpenOptions = IAppPreviewActionOptions & {
  maximize?: boolean;
};

export type IAppPreviewDispatchContext = {
  navigateToRoute: (route: IAppPreviewRoute) => void;
  navigateToUrl: (url: string) => void;
  setErrorMessage: (message: string) => void;
  setOpened: (opened: boolean, options?: IAppPreviewOpenOptions) => void;
  setHidden: (hidden: boolean) => void;
  setIframeRef: (iframeRef?: React.RefObject<HTMLIFrameElement>) => void;
  setSize: (size: ISize, options?: IAppPreviewActionOptions) => void;
  saveState: () => void;
  restoreState: () => void;
};

const stub = (): never => {
  throw new Error(
    'You forgot to wrap your component in <AppPreviewProvider />.',
  );
};

export const AppPreviewDispatchContext =
  createContext<IAppPreviewDispatchContext>({
    navigateToRoute: stub,
    navigateToUrl: stub,
    setErrorMessage: stub,
    setOpened: stub,
    setHidden: stub,
    setIframeRef: stub,
    setSize: stub,
    saveState: stub,
    restoreState: stub,
  });
